import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, articles } from '@/mocks/articles';

const featuredSlugs = [
  'zhengtaifengge-rumen-zhinan',
  'jingdian-zhengtai-manga-pan-dian',
  'renwu-bili-sheji-jiqiao',
];

const featuredArticles = articles.filter((a) => featuredSlugs.includes(a.slug));
const latestArticles = articles.filter((a) => !featuredSlugs.includes(a.slug));

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '正太漫画',
  url: typeof window !== 'undefined' ? window.location.origin : '',
  description: '正太漫画专题页，汇聚各类正太风格漫画作品推荐、绘画技巧解析与同人创作资源。',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: '/search?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '正太漫画研究所',
  url: typeof window !== 'undefined' ? window.location.origin : '',
  description: '正太漫画专题网站，致力于为漫画爱好者与创作者提供优质的正太风格漫画作品推荐、绘画教程与同人创作资源。',
  sameAs: [
    'https://weibo.com/zhengtai-manga',
    'https://space.bilibili.com/zhengtai-manga',
  ],
};

export default function Home() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubscribeStatus('success');
      setEmail('');
      return;
    }
    if (!email) return;

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      formData.set('email', email);
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const res = await fetch('https://readdy.ai/api/form/d9u4r3lca23r0tr1aqk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setEmail('');
      } else {
        setSubscribeStatus('error');
      }
    } catch {
      setSubscribeStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[620px] flex items-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=soft%20dreamy%20anime%20style%20illustration%20young%20boy%20character%20sitting%20under%20cherry%20blossom%20tree%20reading%20a%20book%20warm%20golden%20sunlight%20filtering%20through%20petals%20pastel%20color%20palette%20gentle%20breeze%20atmosphere%20whimsical%20and%20peaceful%20scene%20high%20quality%20digital%20art&width=1600&height=800&seq=hero-bg-01&orientation=landscape"
              alt="正太漫画 - 精选正太风格漫画大全"
              className="absolute inset-0 w-full h-full object-cover"
              title="正太漫画专题"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
            <div className="relative z-10 w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  探索正太漫画的独特美学世界
                </h1>
                <p className="text-white/85 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
                  汇聚正太风格漫画作品推荐、绘画技巧解析与同人创作资源，带你深入了解这一独特的漫画美学风格
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/category/zuopin-tuijian"
                    onClick={scrollToTop}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-book-open-line"></i>
                    浏览作品推荐
                  </Link>
                  <Link
                    to="/category/huihua-jiaocheng"
                    onClick={scrollToTop}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-full font-medium text-sm hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-brush-line"></i>
                    学习绘画教程
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  内容分类
                </h2>
                <p className="text-foreground-600 text-sm md:text-base">
                  按需浏览，快速找到你感兴趣的正太漫画内容
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={scrollToTop}
                    className="group bg-background-100 rounded-xl p-5 hover:bg-accent-50 transition-all duration-300 border border-background-200/70 hover:border-accent-200 cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3 group-hover:bg-accent-200 transition-colors">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    精选推荐
                  </h2>
                  <p className="text-sm text-foreground-600">编辑精选的高质量正太漫画内容</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-600">持续更新的正太漫画相关内容</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            </div>
          </section>

          {/* Newsletter */}
          <section className="py-14 md:py-18 bg-secondary-100">
            <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-mail-send-line text-2xl text-accent-600"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                订阅我们的资讯
              </h2>
              <p className="text-sm text-foreground-600 mb-6">
                每周精选正太漫画资讯、教程更新与同人作品推送，让创作灵感源源不断
              </p>
              <form onSubmit={handleSubscribe} data-readdy-form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入你的邮箱地址"
                  required
                  className="flex-1 px-4 py-2.5 rounded-full border border-background-300 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  立即订阅
                </button>
              </form>
              {subscribeStatus === 'success' && (
                <p className="mt-3 text-sm text-accent-600">订阅成功！感谢你的关注。</p>
              )}
              {subscribeStatus === 'error' && (
                <p className="mt-3 text-sm text-red-500">订阅失败，请稍后重试。</p>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}